/**
 * Where a row on the home opens (D18).
 *
 * The execute surfaces are keyed by link shortId. When the home already knows
 * one — links are minted at creation (D15), and the home reads the entity docs
 * that carry them — it passes it along as `?s=`, and opening becomes a straight
 * render. Without it the page has to trade the id for a link first: one
 * callable round trip behind an "Opening…" spinner.
 *
 * The id stays in the path either way, so the URL still identifies the entity
 * even when the shortId is stale or absent.
 */
export function taskHref(taskId: string, shortId: string | null): string {
  const base = `/home/t/${encodeURIComponent(taskId)}`;
  return shortId ? `${base}?s=${encodeURIComponent(shortId)}` : base;
}

/**
 * A report you sent to somebody else's bucket.
 *
 * No `?s=` here, ever, and not because one is missing: the shortId on a
 * contributed task is an *execute* link, and the resolver refuses anyone who is
 * not the assigned contact. Handing it over would trade a working page for a
 * phone-mismatch screen.
 */
export function sentReportHref(taskId: string): string {
  return `/home/sent/${encodeURIComponent(taskId)}`;
}

/**
 * A lens on the board (D26). `all` is the home itself rather than a page of
 * its own — an unfiltered filter page would be the home with a different URL.
 */
export function lensHref(key: string): string {
  return key === 'all' ? '/' : `/home/lens/${encodeURIComponent(key)}`;
}

/**
 * The Contributed lens with one report open over it — where a desktop reads a
 * report, since `/home/sent/` is the phone's shape. `?open=` is an instruction
 * about this arrival, not a description of the page, so the lens reads it once
 * and closing the modal does not rewrite the URL.
 */
export function contributedLensHref(taskId: string): string {
  return `${lensHref('contributed')}?open=${encodeURIComponent(taskId)}`;
}

export function parseOpenItem(search: string): string | null {
  return new URLSearchParams(search).get('open') || null;
}

export type BucketTab = 'content' | 'review' | 'qr' | 'settings';
export type BucketContentView = 'list' | 'container';

/** The owner's collections (D24). No `?s=`: these are not execute links. */
export function bucketHref(
  bucketId: string,
  tab?: BucketTab,
  view?: BucketContentView,
): string {
  const base = `/home/b/${encodeURIComponent(bucketId)}`;
  const params = new URLSearchParams();
  if (tab && tab !== 'content') params.set('tab', tab);
  if ((!tab || tab === 'content') && view === 'container') params.set('view', 'container');
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function parseBucketTab(search: string): BucketTab {
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'review' || tab === 'qr' || tab === 'settings' ? tab : 'content';
}

export function parseBucketContentView(search: string): BucketContentView {
  return new URLSearchParams(search).get('view') === 'container' ? 'container' : 'list';
}

/** The owner's routine list — sidebar "Routine" / "Rotinas", not an editor. */
export const ROUTINES_PATH = '/routines';

export function routineDisplayName(name: string | null | undefined): string {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || 'Untitled routine';
}

export function routineHref(
  bucketId: string,
  tab?: RoutineTab,
  view?: RoutineView,
  step?: string | null,
): string {
  const base = `/home/routine/${encodeURIComponent(bucketId)}`;
  const params = new URLSearchParams();
  if (tab && tab !== 'edit') params.set('tab', tab);
  if (view && view !== 'list' && (!tab || tab === 'edit')) params.set('view', view);
  if (step && (!tab || tab === 'edit')) params.set('step', step);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * The routine page with the "your run is waiting" card over it.
 *
 * Only the sample door builds this, and only on a desktop. The visitor asked to
 * *walk* a run and the run exists, but a computer cannot walk it — so landing
 * them on `/r/<shortId>` spends the arrival on a bare QR page that says nothing
 * about the routine they just made and offers no way into it. The run's link
 * rides along as a param instead, and the editor opens it as a card: the thing
 * they built, with the way to run it on top.
 */
export function routineRunReadyHref(bucketId: string, runShortId: string): string {
  return `${routineHref(bucketId)}?runReady=${encodeURIComponent(runShortId)}`;
}

export function parseRoutineRunReady(search: string): string | null {
  return new URLSearchParams(search).get('runReady') || null;
}

export type RoutineTab = 'edit' | 'runs' | 'stats' | 'settings';
export type RoutineView = 'list' | 'column' | 'map' | 'grid';

export function parseRoutineTab(search: string): RoutineTab {
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'runs' || tab === 'stats' || tab === 'settings' ? tab : 'edit';
}

export function parseRoutineView(search: string): RoutineView {
  const view = new URLSearchParams(search).get('view');
  if (view === 'column' || view === 'map' || view === 'grid') return view;
  return 'list';
}

export function parseRoutineStep(search: string): string | null {
  return new URLSearchParams(search).get('step');
}

export function contactHref(contactId: string): string {
  return `/home/c/${encodeURIComponent(contactId)}`;
}

export function runHref(runId: string, shortId: string | null): string {
  const base = `/home/r/${encodeURIComponent(runId)}`;
  return shortId ? `${base}?s=${encodeURIComponent(shortId)}` : base;
}
